'use client';

import type { HTMLAttributes } from 'react';

import { twMerge } from 'tailwind-merge';
import { useRef, useState, useEffect } from 'react';
import {
  m,
  useTransform,
  useMotionValue,
  useAnimationFrame,
  useMotionTemplate,
} from 'framer-motion';

import { animateBorderClasses } from '@/core/constants';

type BorderStyleProps = {
  width?: string;
  size?: number;
  className?: string;
};

type AnimateBorderProps = HTMLAttributes<HTMLDivElement> & {
  duration?: number;
  slotProps?: {
    primaryBorder?: BorderStyleProps;
    secondaryBorder?: BorderStyleProps;
    outlineColor?: string;
    svgSettings?: {
      rx?: string;
      ry?: string;
    };
  };
};

export function AnimateBorder({
  children,
  duration,
  slotProps,
  className,
  ...other
}: AnimateBorderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const primaryBorderRef = useRef<HTMLSpanElement>(null);

  const [isHidden, setIsHidden] = useState(false);
  const [secondaryBorderStyles, setSecondaryBorderStyles] = useState({
    padding: '',
    borderRadius: '',
  });

  useEffect(() => {
    const handleVisibility = () => {
      if (rootRef.current) {
        const displayStyle = getComputedStyle(rootRef.current).display;
        setIsHidden(displayStyle === 'none');
      }
    };

    if (rootRef.current) {
      handleVisibility();
      window.addEventListener('resize', handleVisibility);
    }

    return () => {
      window.removeEventListener('resize', handleVisibility);
    };
  }, []);

  useEffect(() => {
    if (primaryBorderRef.current) {
      const style = getComputedStyle(primaryBorderRef.current);
      const isRtl = false; // Assume LTR direction by default

      setSecondaryBorderStyles({
        padding: `${style.paddingBottom} ${isRtl ? style.paddingRight : style.paddingLeft} ${style.paddingTop} ${isRtl ? style.paddingLeft : style.paddingRight}`,
        borderRadius: `${isRtl ? style.borderBottomLeftRadius : style.borderBottomRightRadius} ${isRtl ? style.borderBottomRightRadius : style.borderBottomLeftRadius} ${isRtl ? style.borderTopLeftRadius : style.borderTopRightRadius} ${isRtl ? style.borderTopRightRadius : style.borderTopLeftRadius}`,
      });
    }
  }, []);

  const outlineColor = slotProps?.outlineColor;

  const borderProps = {
    duration,
    isHidden,
    rx: slotProps?.svgSettings?.rx,
    ry: slotProps?.svgSettings?.ry,
  };

  const renderPrimaryBorder = () => (
    <MovingBorder
      {...borderProps}
      ref={primaryBorderRef}
      size={slotProps?.primaryBorder?.size}
      className={twMerge(
        animateBorderClasses.primaryBorder,
        slotProps?.primaryBorder?.width ? `p-${slotProps.primaryBorder.width}` : 'p-1',
        'bg-gradient-to-r from-blue-500 to-purple-600',
        slotProps?.primaryBorder?.className
      )}
    />
  );

  const renderSecondaryBorder = () =>
    slotProps?.secondaryBorder && (
      <MovingBorder
        {...borderProps}
        size={slotProps?.secondaryBorder?.size ?? slotProps?.primaryBorder?.size}
        className={twMerge(
          animateBorderClasses.secondaryBorder,
          'bg-gradient-to-r from-blue-500 to-purple-600',
          slotProps?.secondaryBorder?.width ? `p-${slotProps.secondaryBorder.width}` : 'p-1',
          'scale-[-1]',
          slotProps?.secondaryBorder?.className
        )}
        style={{ borderRadius: secondaryBorderStyles.borderRadius }}
      />
    );

  const getBorderGradientClasses = () => {
    if (!outlineColor) return 'before:bg-gradient-to-r before:from-blue-500 before:to-purple-600';
    return 'before:bg-[var(--outline-color)]';
  };

  // Set custom outline color as a style if provided
  const outlineStyle: React.CSSProperties & {
    '--outline-color'?: string;
  } = {
    '--outline-color': outlineColor || undefined,
  };

  return (
    <div
      dir="ltr"
      ref={rootRef}
      className={twMerge(
        animateBorderClasses.root,
        'min-w-[40px] min-h-[40px] overflow-hidden relative w-fit',
        'before:absolute before:inset-0 before:content-[""] before:z-0',
        getBorderGradientClasses(),
        slotProps?.primaryBorder?.width
          ? `before:p-${slotProps.primaryBorder.width}`
          : 'before:p-1',
        children ? 'min-w-0 min-h-0' : '',
        className
      )}
      style={outlineStyle}
      {...other}
    >
      {renderPrimaryBorder()}
      {renderSecondaryBorder()}
      {children}
    </div>
  );
}

// ----------------------------------------------------------------------

type MovingBorderProps = HTMLAttributes<HTMLSpanElement> & {
  rx?: string;
  ry?: string;
  duration?: number;
  isHidden?: boolean;
  size?: BorderStyleProps['size'];
  ref?: React.Ref<HTMLSpanElement>;
};

function MovingBorder({
  className,
  size,
  isHidden,
  rx = '30%',
  ry = '30%',
  duration = 8,
  style,
  ...other
}: MovingBorderProps) {
  const svgRectRef = useRef<SVGRectElement>(null);
  const progress = useMotionValue<number>(0);

  const updateAnimationFrame = (time: number) => {
    if (!svgRectRef.current) return;
    try {
      const pathLength = svgRectRef.current.getTotalLength();
      const pixelsPerMs = pathLength / (duration * 1000);
      progress.set((time * pixelsPerMs) % pathLength);
    } catch {
      return;
    }
  };

  const calculateTransform = (val: number) => {
    if (!svgRectRef.current) return { x: 0, y: 0 };
    try {
      const point = svgRectRef.current.getPointAtLength(val);
      return point ? { x: point.x, y: point.y } : { x: 0, y: 0 };
    } catch {
      return { x: 0, y: 0 };
    }
  };

  useAnimationFrame((time) => (!isHidden ? updateAnimationFrame(time) : undefined));

  const x = useTransform(progress, (val) => calculateTransform(val).x);
  const y = useTransform(progress, (val) => calculateTransform(val).y);
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <span className={twMerge('text-left', className)} style={style} {...other}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        className={twMerge(animateBorderClasses.svgWrapper, 'absolute')}
      >
        <rect ref={svgRectRef} fill="none" width="100%" height="100%" rx={rx} ry={ry} />
      </svg>

      <m.span
        style={{
          transform,
          width: size,
          height: size,
        }}
        className={twMerge(
          animateBorderClasses.movingShape,
          'blur-lg absolute bg-gradient-radial from-current via-current to-transparent'
        )}
      />
    </span>
  );
}
